import { useEffect, useState, useCallback } from "react";
import { Star, MessageCircle, Send } from "lucide-react";
import { api } from "../api";
import { useI18n, useL } from "../i18n";
import { toast } from "sonner";

export default function Reviews({ destId }) {
  const { t, lang } = useI18n();
  const L = useL();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    () => api.get(`/destinations/${destId}/reviews`).then((r) => setData(r.data)),
    [destId]
  );
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1 || text.trim().length < 3) {
      toast.error(L({ ar: "أضف تقييماً ونصاً قصيراً", en: "Add a rating and short text", fr: "Ajoutez une note et un texte court" }));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/destinations/${destId}/reviews`, { author: author || "Guest", rating, text });
      setText(""); setRating(0); setAuthor("");
      toast.success(L({ ar: "شكراً لمشاركتك ✓", en: "Thanks for sharing ✓", fr: "Merci pour votre avis ✓" }));
      await load();
    } catch (err) {
      toast.error(L({ ar: "تعذّر الإرسال", en: "Submission failed", fr: "Envoi échoué" }));
    } finally { setSubmitting(false); }
  };

  return (
    <section data-testid="reviews-section">
      <h2 className="font-display text-2xl text-[#1C1917] mb-5 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-[#C85A32]" />
        {L({ ar: "آراء المسافرين", en: "Traveler reviews", fr: "Avis des voyageurs" })}
        {data.count > 0 && (
          <span className="inline-flex items-center gap-1 ms-2 text-sm font-normal text-[#57534E]">
            <Star className="h-4 w-4 fill-[#D97706] text-[#D97706]" />
            {data.average} · {data.count} {L({ ar: "تقييم", en: "reviews", fr: "avis" })}
          </span>
        )}
      </h2>

      {/* Form */}
      <form onSubmit={submit} className="rounded-3xl bg-white border border-[#E6DED3] p-5 mb-5 space-y-3">
        <div className="flex items-center gap-1" data-testid="review-star-picker">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || rating) >= n;
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                data-testid={`review-star-${n}`}
                className="p-1 transition-transform hover:scale-110"
                aria-label={`${n} star`}
              >
                <Star className={`h-6 w-6 ${active ? "fill-[#D97706] text-[#D97706]" : "text-[#78716C]"}`} />
              </button>
            );
          })}
          <span className="text-xs text-[#78716C] ms-2">
            {rating > 0 ? `${rating}/5` : L({ ar: "قيّم هذه الوجهة", en: "Rate this destination", fr: "Notez cette destination" })}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3">
          <input
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder={L({ ar: "الاسم (اختياري)", en: "Name (optional)", fr: "Nom (facultatif)" })}
            data-testid="review-author"
            className="h-11 px-4 rounded-xl bg-[#F3EDE4] focus:bg-white border border-transparent focus:border-[#1C1917] outline-none text-sm"
          />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={L({ ar: "شارك نصيحة أو انطباعاً…", en: "Share a tip or impression…", fr: "Partagez une astuce ou impression…" })}
            data-testid="review-text"
            className="h-11 px-4 rounded-xl bg-[#F3EDE4] focus:bg-white border border-transparent focus:border-[#1C1917] outline-none text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="review-submit"
          className="h-11 px-5 rounded-full bg-[#1C1917] text-[#FAF7F2] hover:bg-[#C85A32] disabled:opacity-50 text-sm font-medium inline-flex items-center gap-2 transition-colors"
        >
          <Send className="h-3.5 w-3.5" />
          {L({ ar: "أرسل التقييم", en: "Submit review", fr: "Publier l'avis" })}
        </button>
      </form>

      {/* List */}
      <div className="space-y-3" data-testid="reviews-list">
        {data.reviews.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#78716C]">
            {L({ ar: "كن أول من يشارك تجربته", en: "Be the first to share your experience", fr: "Soyez le premier à partager votre expérience" })}
          </div>
        ) : (
          data.reviews.map((r) => (
            <article key={r.id} className="rounded-2xl bg-white border border-[#E6DED3] p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-[#F3EDE4] text-[#C85A32] grid place-items-center text-xs font-semibold uppercase">
                    {(r.author || "?").charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#1C1917]">{r.author}</div>
                    <div className="text-[10px] text-[#78716C]">{new Date(r.timestamp).toLocaleDateString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-US")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-[#D97706] text-[#D97706]" : "text-[#E6DED3]"}`} />
                  ))}
                </div>
              </div>
              <p className="mt-2 text-sm text-[#1C1917] leading-relaxed">{r.text}</p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}
